import { QuestionBox } from "@ui/organisms/QuestionBox/QuestionBox";
import {
	defaultQuestionEffort,
	type QuestionEffort,
} from "@ui/organisms/QuestionBox/questionEfforts";
import { Text } from "@ui/typography/Text";
import { pluralise } from "@utils/text";
import { createSignal } from "solid-js";
import { startChat } from "~/modules/shared/chat/chat-panel/startChat";
import { signInUrl } from "~/modules/shared/utils/auth";
import { NewQuestionDiscoveryCard } from "./components/NewQuestionDiscoveryCard";
import { pageVars } from "./utils/pageVars";

export const DiscoveryAsk = () => {
	const vars = pageVars();
	const [busy, setBusy] = createSignal(false);
	const [effort, setEffort] = createSignal<QuestionEffort>(
		defaultQuestionEffort(),
	);
	const entitled = vars.currentlyEntitledCreatorCount ?? 0;
	const canSubscriptions = vars.isSignedIn && entitled >= 1;
	const canPublic = vars.isSignedIn && Boolean(vars.pool_id);
	const canAsk = canSubscriptions || canPublic;
	const isDisabled = () => busy() || !vars.isSignedIn || !canAsk;

	const creatorsNotice = () => {
		if (canSubscriptions) {
			return undefined;
		}
		return vars.isSignedIn
			? "You haven't subscribed to any creators yet"
			: "You don't subscribe to any creators yet";
	};

	const contextDetail = () => {
		if (canSubscriptions) {
			return `${entitled} ${pluralise(entitled, "creator")}`;
		}
		return vars.isSignedIn ? "Public pool" : "Guest";
	};

	const placeholder = () =>
		vars.isSignedIn
			? "Ask anything…"
			: "Subscribe to a creator to start asking...";

	const submit = async (prompt: string, clearQuestion: () => void) => {
		if (busy()) {
			return;
		}
		if (!vars.isSignedIn) {
			window.location.href = signInUrl();
			return;
		}
		if (!canAsk) {
			return;
		}
		setBusy(true);
		await startChat({
			prompt,
			scope: canSubscriptions ? "subscriptions" : "public_pool",
			pool_id: canSubscriptions ? undefined : vars.pool_id,
			effort: effort(),
			clearQuestion,
		});
		setBusy(false);
	};

	return (
		<section class="askgvt-hero askgvt-hero--ask">
			<Text
				as="h1"
				family="display"
				size="xlarge"
				weight="bold"
				color="ink"
				center
				style={{ "letter-spacing": "-0.01em" }}
			>
				What do you want to know?
			</Text>
			<QuestionBox
				scope={canSubscriptions ? "subscriptions" : "public_pool"}
				poolId={canSubscriptions ? undefined : vars.pool_id}
				contextTabs={canSubscriptions ? ["channels", "videos"] : ["videos"]}
				channelsTabLabel="Creators"
				contextSource="pool"
				contextCreators="subscriptions"
				completionType="influencer"
				mentions={vars.isSignedIn}
				redirectURL=""
				placeholder={placeholder()}
				buttonLabel="Ask"
				buttonTheme={canAsk ? "mint-grad" : "grey"}
				disabled={isDisabled()}
				isLoading={busy()}
				onAsk={(prompt, clearQuestion) => void submit(prompt, clearQuestion)}
				creators={[]}
				subscriptionCount={entitled}
				creatorsNotice={creatorsNotice()}
				contextDetail={contextDetail()}
				effort={effort()}
				onEffortChange={setEffort}
			/>
			<div style={{ "margin-top": "2rem" }}>
				<NewQuestionDiscoveryCard />
			</div>
		</section>
	);
};
